// 装饰器：参数校验
import ErrorCode from '../../common/error-code';
import RtcError from '../../common/rtc-error';
import {
  getValueType,
  isFunction,
  isPlainObject,
  isNumber,
  isString,
  isUndefined,
  isObject,
  isArray,
  getConstructorName,
  isConstructor
} from '../utils';
import loggerManager from '../log/logger-manager';
import { generateErrorMessage } from '../../common/error-generator';
import { ErrorName } from '../../common/error-info';
import { createDecorator, ProcessHandler } from './middleware';
import { BASIC_TYPE } from '../../enums';
export interface ValidateRule {
  required?: boolean;
  defaultValue?: any;
  type?: BASIC_TYPE | Array<BASIC_TYPE> | any;
  allowEmpty?: boolean;
  notLessThanZero?: boolean;
  min?: number;
  max?: number;
  instanceOf?: string | object;
  name?: string;
  values?: any;
  properties?: {
    [key: string]: Readonly<ValidateRule>;
  };
  arrayItem?: Readonly<ValidateRule>;
  validate?: (value: any, key: string, fnName: string, className: string, target: any) => void;
}
/**
 * 装饰器：参数校验（用于校验同步方法）
 * 可传入多个 Rule，位置与 arguments 一一对应，用于校验 arguments
 * @param {...ValidateRule} configs
 * @export
 * @return {Function}
 * @example
 * @validate({
 *   name: 'clientConfig',
 *   required: true,
 *   type: BASIC_TYPE.Object,
 *   properties: {
 *     sdkAppId: {
 *       required: true,
 *       type: BASIC_TYPE.Number,
 *       allowEmpty: false
 *     },
 *     userId: {
 *       required: true,
 *       type: BASIC_TYPE.String,
 *       allowEmpty: false
 *     },
 *     ...
 *   }
 * })
 * createClient(clientConfig) {
 */
export function validate(...configs: ValidateRule[]) {
  return createDecorator((next: ProcessHandler, fnName: string) => function (this: any, ...args) {
    doValidate.call(this, configs, args, fnName, this._name);
    return next.apply(this, args);
  });
}

/**
 * 装饰器：参数校验（用于校验异步方法）
 * 可传入多个 Rule，位置与 arguments 一一对应，用于校验 arguments
 * @param  {...ValidateRule} configs
 * @returns {Function}
 * @example
 * @validateAsync({
 *   name: 'stream',
 *   required: true,
 *   instanceOf: LocalStream,
 *   validate: function(stream) {
 *     if (stream !== this._localStream) {
 *       throw new RtcError({
 *         code: ErrorCode.INVALID_PARAMETER,
 *         message: 'stream has not been published yet'
 *       });
 *     }
 *   }
 * })
 * async unpublish() {...}
 */
export function validateAsync(...configs: ValidateRule[]) {
  // target: 装饰器所在类的原型，例如在 Client.join 接口加了参数校验，则 target 为 Client.prototype。
  // fnName: 装饰器修饰的函数名，例如在 Client.join 接口加了参数校验，则 fnName 为 'join'。
  // descriptor: 装饰器修饰函数的属性描述对象，参考：https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperty
  return function (target: any, fnName: string, descriptor: any) {
    // this: 装饰器所在类的实例，例如在 Client.join 接口加了参数校验，则 this 为 client 实例。
    const oldFn = descriptor.value;
    // 改写函数，加上参数校验逻辑。
    // 例如在 Client.join 接口加了参数校验，则此处改写了 Client.join 接口，加了一层参数校验封装。
    descriptor.value = async function (...args: any) {
      doValidate.call(this, configs, args, fnName, this._name);
      return oldFn.apply(this, args);
    };
    return descriptor;
  };
}

/**
 * 执行参数校验
 * @param  {...ValidateRule} configs 参数校验配置信息
 * @param {...any} args 调用接口传的参数值
 * @param {String} fnName 函数名
 */
function doValidate(this: any, configs: ValidateRule | ValidateRule[], args: any[], fnName: string, className: string) {
  try {
    if (isArray(configs)) {
      // 根据 @validate 传入的配置，逐个校验客户调用接口传入的参数
      for (let i = 0; i < configs.length; i++) {
        // 1. 校验传入接口参数的合法性
        check.call(this, {
          rule: configs[i],
          value: args[i],
          key: configs[i].name,
          fnName,
          className
        });
      }
    } else {
      check.call(this, {
        rule: configs,
        value: args[0],
        key: configs.name,
        fnName,
        className
      });
    }
  } catch (error) {
    loggerManager.error(error);
    throw error;
  }
}

/**
 * 根据 rule 校验 value 的合法性，如果校验未通过，则 throw RtcError
 * @param {ValidateRule} rule 参数校验规则
 * @param {*} value 接入侧传入的参数值
 * @param {String} [key] 参数的 key
 * @param {String} fnName 调用的函数名
 */
function check(this: any, { rule, value, key, fnName, className }: { rule: ValidateRule, value: any, key?: string, fnName: string, className: string; }) {
  // 用户没传指定参数
  if (isUndefined(value)) {
    // 检查必填参数，若配置是必填则报错
    if (rule.required) {
      throw new RtcError({
        code: ErrorCode.INVALID_PARAMETER,
        message: generateErrorMessage({
          key: ErrorName.INVALID_PARAMETER_REQUIRED,
          data: { key, rule, fnName, value },
          link: { className, fnName }
        })
      });
    } else {
      // 非必填参数，且没有默认值，跳过
      if (isUndefined(rule.defaultValue)) {
        return;
      }
      value = rule.defaultValue;
    }
  }
  // 检查参数类型 通过数组传入，例如：type: ['boolean', 'string', 'number']
  if (Array.isArray(rule.type)) {
    let isValidated = false;
    for (let i = 0; i < rule.type.length; i++) {
      if (rule.type[i] === null && value === null) {
        isValidated = true;
      }
      if (isFunction(rule.type[i]) && value instanceof rule.type[i]) {
        isValidated = true;
      }
      if (isString(rule.type[i]) && getValueType(value) === rule.type[i].toLowerCase()) {
        isValidated = true;
      }
    }
    if (!isValidated) {
      throw new RtcError({
        code: ErrorCode.INVALID_PARAMETER,
        message: generateErrorMessage({
          key: ErrorName.INVALID_PARAMETER_TYPE,
          data: {
            key,
            // 将 type 数组中各种类型转成字符串
            // ['string', null, HTMLElement] => ['string', 'null', 'HTMLElement']
            rule: {
              type: rule.type.map((item: any) => {
                if (isConstructor(item)) return getConstructorName(item);
                if (isString(item)) return item;
                return getValueType(item);
              })
            },
            fnName, value
          },
          link: { className, fnName }
        })
      });
    }
  } else if (!isUndefined(rule.type) && getValueType(value) !== rule.type) {
    // 直接传入类型，例如：type: 'string' 则直接判断类型是否相等即可
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.INVALID_PARAMETER_TYPE,
        data: { key, rule, fnName, value },
        link: { className, fnName }
      })
    });
  }

  // 不允许传空值，例如 0, '', '  ', NaN
  if (rule.allowEmpty === false) {
    const isEmptyNumber = isNumber(value) && (value === 0 || Number.isNaN(value));
    const isEmptyString = isString(value) && value.trim() === '';
    if (isEmptyNumber || isEmptyString) {
      throw new RtcError({
        code: ErrorCode.INVALID_PARAMETER,
        message: generateErrorMessage({
          key: ErrorName.INVALID_PARAMETER_EMPTY,
          data: { key, rule, fnName, value },
          link: { className, fnName }
        })
      });
    }
  }

  // 要求值必须大于等于0
  if (rule.notLessThanZero && isNumber(value) && value < 0) {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.CANNOT_LESS_THAN_ZERO,
        data: { key, rule, fnName, value },
        link: { className, fnName }
      })
    });
  }
  // min value
  if (!isUndefined(rule.min) && isNumber(value) && value < rule.min) {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.INVALID_PARAMETER_MIN,
        data: { key, rule, fnName, value },
        link: { className, fnName }
      })
    });
  }
  // max value
  if (!isUndefined(rule.max) && isNumber(value) && value > rule.max) {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.INVALID_PARAMETER_MAX,
        data: { key, rule, fnName, value },
        link: { className, fnName }
      })
    });
  }

  // 检查 instanceof
  if (isString(rule.instanceOf)) {
    // 检查是否是 sdk 内部类的实例
    if (!value || value._name !== rule.instanceOf) {
      throw new RtcError({
        code: ErrorCode.INVALID_PARAMETER,
        message: generateErrorMessage({
          key: ErrorName.INVALID_PARAMETER_INSTANCE,
          data: { key, rule, fnName, value },
          link: { className, fnName }
        })
      });
    }
  } else if (isFunction(rule.instanceOf) && !(value instanceof rule.instanceOf)) {
    // 检查是否是全局类的实例，例如 MediaStreamTrack
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.INVALID_PARAMETER_INSTANCE,
        data: { key, rule, fnName, value },
        link: { className, fnName }
      })
    });
  }
  // 检查参数值是否在指定范围内
  if (rule.values && !rule.values.includes(value)) {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: generateErrorMessage({
        key: ErrorName.INVALID_PARAMETER_RANGE,
        data: { key, rule, fnName, value },
        link: { className, fnName }
      })
    });
  }

  // 若参数是 Object，则此处校验 Object 所有属性(properties)的合法性
  const { properties } = rule;
  if (isPlainObject(properties) && isObject(value)) {
    Object.keys(properties).forEach(innerKey => {
      check.call(this, {
        rule: properties[innerKey],
        value: value && value[innerKey],
        key: `${key}.${innerKey}`,
        fnName,
        className
      });
    });
  }

  // 若参数是 Array，则此处校验 Array 中所有元素的属性的合法性
  const { arrayItem } = rule;
  if (isPlainObject(arrayItem) && isArray(value)) {
    value.forEach((item, index) => {
      check.call(this, {
        rule: arrayItem,
        value: item,
        key: `${key}[${index}]`,
        fnName,
        className
      });
    });
  }

  // 自定义 validate function
  if (isFunction(rule.validate)) {
    rule.validate.call(this, value, key!, fnName, className, this);
  }
}
